/**
 * Organizations Page
 * Lists all organizations with search, filter, pagination, and CRUD operations.
 * Only accessible to global_admin users.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Dropdown,
  Option,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Text,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Add20Regular,
  Delete20Regular,
  Edit20Regular,
  ArrowClockwise20Regular,
  People20Regular,
  Organization20Regular,
  Open20Regular,
} from '@fluentui/react-icons';
import { OrganizationApiService } from '../services/organizationApi';
import { Alert } from '../components/common/Alert';
import { OrganizationForm } from '../components/Organizations/OrganizationForm';
import {
  PageLayout,
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
  ToolbarSearch,
  createStatusColumn,
  createDateColumn,
} from '../components/layouts';
import { createColumnWithRowActions } from '../components/layouts/factories/columns';
import { useOrganizations } from '../hooks/useOrganizations';
import type { OrganizationWithCounts, OrganizationStatus } from '../types/organization';

const useStyles = makeStyles({
  statusFilter: {
    minWidth: '150px',
  },
  countBadge: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
  clickableName: {
    cursor: 'pointer',
    color: tokens.colorBrandForeground1,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  authCell: {
    display: 'flex',
    flexDirection: 'column',
  },
  maxUsersText: {
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Organizations Page Component
 */
export const OrganizationsPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrganizationStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Selection state for bulk operations
  const [selectedOrgs, setSelectedOrgs] = useState<Set<number>>(new Set());

  // React Query hook for organizations data
  const {
    organizations,
    totalPages,
    totalOrganizations,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useOrganizations({
    page: currentPage,
    pageSize,
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  // Create organization dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Edit organization dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrganizationWithCounts | null>(null);

  // Delete organization dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState<OrganizationWithCounts | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Bulk delete dialog
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);

  /**
   * Handle search query changes
   */
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  /**
   * Handle status filter changes
   */
  const handleStatusFilterChange = (value: OrganizationStatus | 'all') => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  /**
   * Handle create organization success
   */
  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    refetch();
  };

  /**
   * Handle edit button click
   */
  const handleEditClick = (org: OrganizationWithCounts) => {
    setEditingOrg(org);
    setShowEditDialog(true);
  };

  /**
   * Handle edit organization success
   */
  const handleEditSuccess = () => {
    setShowEditDialog(false);
    setEditingOrg(null);
    refetch();
  };

  /**
   * Handle delete button click
   */
  const handleDeleteClick = (org: OrganizationWithCounts) => {
    setDeletingOrg(org);
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  /**
   * Handle delete organization
   */
  const handleDeleteOrganization = async () => {
    if (!deletingOrg) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await OrganizationApiService.deleteOrganization(deletingOrg.id);
      setShowDeleteDialog(false);
      setDeletingOrg(null);
      refetch();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete organization');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle bulk delete button click
   */
  const handleBulkDeleteClick = () => {
    setBulkDeleteError(null);
    setShowBulkDeleteDialog(true);
  };

  /**
   * Handle bulk delete organizations
   */
  const handleBulkDeleteOrganizations = async () => {
    if (selectedOrgs.size === 0) return;

    setIsBulkDeleting(true);
    setBulkDeleteError(null);

    try {
      const deletePromises = Array.from(selectedOrgs).map((id) =>
        OrganizationApiService.deleteOrganization(id)
      );
      await Promise.all(deletePromises);
      setShowBulkDeleteDialog(false);
      setSelectedOrgs(new Set());
      refetch();
    } catch (err) {
      setBulkDeleteError(err instanceof Error ? err.message : 'Failed to delete organizations');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  /**
   * Define table columns
   */
  const columns: TableColumnDefinition<OrganizationWithCounts>[] = [
    createColumnWithRowActions<OrganizationWithCounts>({
      columnId: 'name',
      header: 'Organization Name',
      compare: (a, b) => a.name.localeCompare(b.name),
      renderContent: (item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Avatar
            name={item.name}
            size={32}
            color="colorful"
          />
          <div
            className={styles.clickableName}
            onClick={() => navigate(`/app/organizations/${item.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                navigate(`/app/organizations/${item.id}`);
              }
            }}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Text weight="semibold">{item.name}</Text>
          </div>
        </div>
      ),
      getActions: (item) => [
        {
          label: 'View Details',
          icon: <Open20Regular />,
          onClick: () => navigate(`/app/organizations/${item.id}`),
        },
        {
          label: 'Edit',
          icon: <Edit20Regular />,
          onClick: () => handleEditClick(item),
        },
        {
          label: 'Delete',
          icon: <Delete20Regular />,
          onClick: () => handleDeleteClick(item),
        },
      ],
      getAriaLabel: (item) => `Actions for ${item.name}`,
    }),
    createStatusColumn<OrganizationWithCounts>({
      columnId: 'status',
      header: 'Status',
      getStatus: (item) => ({
        label: item.status,
        type: item.status === 'active' ? 'success' : item.status === 'suspended' ? 'warning' : 'informative',
      }),
    }),
    createTableColumn<OrganizationWithCounts>({
      columnId: 'authentication',
      compare: (a, b) => a.authentication_mode.localeCompare(b.authentication_mode),
      renderHeaderCell: () => 'Authentication',
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.authCell}>
            <Text>{item.authentication_mode === 'federated' ? 'Federated' : 'Home Tenant'}</Text>
            {item.authentication_mode === 'federated' && item.max_users && (
              <Text size={200} className={styles.maxUsersText}>
                User Limit: {item.max_users}
              </Text>
            )}
          </div>
        </TableCellLayout>
      ),
    }),
    createTableColumn<OrganizationWithCounts>({
      columnId: 'user_count',
      compare: (a, b) => a.user_count - b.user_count,
      renderHeaderCell: () => 'Users',
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.countBadge}>
            <People20Regular />
            <Text>{item.user_count}</Text>
          </div>
        </TableCellLayout>
      ),
    }),
    createTableColumn<OrganizationWithCounts>({
      columnId: 'tenant_count',
      compare: (a, b) => a.tenant_count - b.tenant_count,
      renderHeaderCell: () => 'Tenants',
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.countBadge}>
            <Organization20Regular />
            <Text>{item.tenant_count}</Text>
          </div>
        </TableCellLayout>
      ),
    }),
    createDateColumn<OrganizationWithCounts>({
      columnId: 'created_at',
      header: 'Created',
      getDate: (item) => new Date(item.created_at),
      format: 'short',
    }),
  ];

  /**
   * Render the toolbar for the DataGridCard
   */
  const renderToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => refetch()}
          loading={isFetching}
          loadingText="Refreshing..."
        >
          Refresh
        </ToolbarButton>
        <Dropdown
          className={styles.statusFilter}
          placeholder="Status"
          value={statusFilter === 'all' ? 'All Statuses' : statusFilter}
          onOptionSelect={(_, data) => {
            const value = data.optionValue as OrganizationStatus | 'all';
            handleStatusFilterChange(value);
          }}
        >
          <Option value="all">All Statuses</Option>
          <Option value="active">Active</Option>
          <Option value="suspended">Suspended</Option>
        </Dropdown>
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        <ToolbarSearch
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search organizations..."
        />
        <ToolbarButton
          icon={<Delete20Regular />}
          onClick={handleBulkDeleteClick}
          disabled={selectedOrgs.size === 0}
        >
          {selectedOrgs.size > 0 ? `Delete (${selectedOrgs.size})` : 'Delete'}
        </ToolbarButton>
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => setShowCreateDialog(true)}
        >
          Add Organization
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  return (
    <PageLayout title="Organizations" testId="organizations-page">
      {/* Error Alert */}
      {error && (
        <Alert intent="error">
          <Text>{error.message}</Text>
        </Alert>
      )}

      {/* Data Grid */}
      <DataGridCard
        items={organizations}
        columns={columns}
        getRowId={(item) => item.id}
        columnSizingOptions={{
          name: { minWidth: 200, defaultWidth: 300 },
          status: { minWidth: 80, defaultWidth: 100 },
          user_count: { minWidth: 80, defaultWidth: 100 },
          tenant_count: { minWidth: 80, defaultWidth: 100 },
          created_at: { minWidth: 100, defaultWidth: 120 },
        }}
        toolbar={renderToolbar()}
        pagination={{
          showing: organizations.length,
          total: totalOrganizations,
          page: currentPage,
          totalPages: totalPages || 1,
          onPageChange: setCurrentPage,
        }}
        loading={isLoading}
        emptyMessage="No organizations found"
        testId="organizations-table"
        selectable
        selectedIds={selectedOrgs}
        onSelectionChange={(ids) => setSelectedOrgs(ids as Set<number>)}
      />

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(_, data) => setShowCreateDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogContent>
              <OrganizationForm
                onSuccess={handleCreateSuccess}
                onCancel={() => setShowCreateDialog(false)}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(_, data) => setShowEditDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogContent>
              <OrganizationForm
                organization={editingOrg ?? undefined}
                onSuccess={handleEditSuccess}
                onCancel={() => setShowEditDialog(false)}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogContent>
              {deleteError && (
                <div style={{ marginBottom: '16px' }}>
                  <Alert intent="error">
                    <Text>{deleteError}</Text>
                  </Alert>
                </div>
              )}
              {deletingOrg &&
              (deletingOrg.user_count > 0 ||
                deletingOrg.tenant_count > 0 ||
                deletingOrg.pending_request_count > 0) ? (
                <>
                  <Alert intent="error">
                    <Text weight="semibold">Cannot delete this organization</Text>
                  </Alert>
                  <div style={{ marginTop: '16px' }}>
                    <Text>
                      Before deleting <strong>{deletingOrg.name}</strong>, you must first remove
                      or reassign all associated resources:
                    </Text>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {deletingOrg.user_count > 0 && (
                        <li>
                          <Text>{deletingOrg.user_count} user(s) assigned</Text>
                        </li>
                      )}
                      {deletingOrg.tenant_count > 0 && (
                        <li>
                          <Text>{deletingOrg.tenant_count} tenant(s) assigned</Text>
                        </li>
                      )}
                      {deletingOrg.pending_request_count > 0 && (
                        <li>
                          <Text>{deletingOrg.pending_request_count} pending tenant request(s)</Text>
                        </li>
                      )}
                    </ul>
                  </div>
                </>
              ) : (
                <Text>
                  Are you sure you want to delete <strong>{deletingOrg?.name}</strong>?
                  This action cannot be undone.
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeleteOrganization}
                disabled={
                  isDeleting ||
                  (deletingOrg !== null &&
                    (deletingOrg.user_count > 0 ||
                      deletingOrg.tenant_count > 0 ||
                      deletingOrg.pending_request_count > 0))
                }
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
              >
                {isDeleting ? <Spinner size="tiny" /> : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={(_, data) => setShowBulkDeleteDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Organizations</DialogTitle>
            <DialogContent>
              {bulkDeleteError && (
                <div style={{ marginBottom: '16px' }}>
                  <Alert intent="error">
                    <Text>{bulkDeleteError}</Text>
                  </Alert>
                </div>
              )}
              <Text>
                Are you sure you want to delete <strong>{selectedOrgs.size} organization(s)</strong>?
                This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setShowBulkDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleBulkDeleteOrganizations}
                disabled={isBulkDeleting}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
              >
                {isBulkDeleting ? <Spinner size="tiny" /> : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </PageLayout>
  );
};
